import type { SerializedStyles } from '@emotion/react';
import {
  useCallback, useEffect, useState,
} from 'react';
import { GoogleMapComponent } from '~/_shared/components/google-map/google-map.component';
import type { GoogleMapInstance } from '~/_shared/components/google-map/useGetGoogleMapInstance.hook';
import type { LatLng } from '~/_shared/types/latLng';
import { delay } from '~/_shared/utils/delay';
import { ExportReportType } from '~/exportReport/exportReportType';

export type ExportReportMapProps = Readonly<{
  id: string;
  css?: SerializedStyles;
  className?: string;
  onMapLoaded?: () => void;
  center?: LatLng;
  paths?: Array<LatLng>;
  type: ExportReportType;
  mapType: 'roadmap' | 'hybrid';
}>;

export const ExportReportMapComponent = ({ id, className, onMapLoaded, mapType, type, center, paths }: ExportReportMapProps) => {
  const [map, setMap] = useState<GoogleMapInstance>();

  const handleMapInit = useCallback((map: GoogleMapInstance) => {
    setMap(map);

    if (!onMapLoaded) {
      return;
    }

    // ensure all items loaded on the map
    delay(1000).then(() => {
      onMapLoaded();
    });
  }, [onMapLoaded]);

  // RADIUS
  useEffect(() => {
    if (!map || type !== ExportReportType.Radius || !center) {
      return;
    }

    map.instance.setOptions({
      center,
      mapTypeId: mapType,
      disableDefaultUI: true,
    });

    const circle = new google.maps.Circle({
      map: map.instance,
      center,
      radius: 10000, // 10km
      fillColor: '#0078CE',
      fillOpacity: 0.5,
      strokeColor: '#0078CE',
      strokeOpacity: 1,
      strokeWeight: 2,
    });

    const circleBounds = circle.getBounds();

    if (circleBounds) {
      map.instance.fitBounds(circleBounds);
    }

    return () => {
      if (circle) {
        circle.setMap(null);
      }
    };
  }, [center, map, mapType, type]);

  // LOCATION
  useEffect(() => {
    if (!map || type !== ExportReportType.Location || !center) {
      return;
    }

    map.instance.setOptions({
      center,
      mapTypeId: mapType,
      zoom: 15,
      disableDefaultUI: true,
      gestureHandling: 'none',
    });
  }, [center, map, mapType, type]);

  // POLYGON
  useEffect(() => {
    if (!map || type !== ExportReportType.DriveTimePolygon || !paths) {
      return;
    }

    map.instance.setOptions({
      mapTypeId: mapType,
      zoom: 10,
      disableDefaultUI: true,
      gestureHandling: 'none',
    });

    const polygon = new google.maps.Polygon({
      paths,
      map: map.instance,
      fillColor: '#0078CE',
      fillOpacity: 0.5,
      strokeColor: '#0078CE',
      strokeOpacity: 1,
      strokeWeight: 2,
    });

    const polygonBounds = new google.maps.LatLngBounds();

    paths.forEach((latLng) => {
      polygonBounds.extend(latLng);
    });

    map.instance.fitBounds(polygonBounds);

    return (() => {
      if (polygon) {
        polygon.setMap(null);
      }
    });
  }, [map, mapType, paths, type]);

  return (
    <GoogleMapComponent
      className={className}
      onMapInitialized={handleMapInit}
      id={id}
    />
  );
};
